import React, { useMemo, useRef, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Modal, Pressable, Platform, StyleProp, ViewStyle } from 'react-native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { MESSAGE_SORT_OPTIONS, getSortOptionLabel, type MessageSortOption } from '@/constants/residents/messages';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import type { AnchorRect } from '@/hooks/common/useAnchoredPosition';

const IS_WEB = Platform.OS === 'web';

const SearchIcon = RESIDENTS_SVG_COMPONENTS.MAGNIFYING_GLASS;
const PencilIcon = SVG_COMPONENTS.PENCIL;
const ChevronDownIcon = RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN;

export type SortOption = MessageSortOption;

export interface SortOptionItem<TKey extends string = string> {
  key: TKey;
  label: string;
}

export interface ChatGlassListHeaderProps<TKey extends string = string> {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onComposePress?: () => void;
  composeIcon?: React.ReactNode;
  sortOption?: TKey;
  sortOptions?: SortOptionItem<TKey>[];
  onSortChange?: (key: TKey) => void;
  hideSortRow?: boolean;
  placeholder?: string;
  rightActions?: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
}

const DEFAULT_SORT_OPTIONS: SortOptionItem<MessageSortOption>[] = MESSAGE_SORT_OPTIONS.map((opt) => ({
  key: opt.key,
  label: opt.label,
}));

function ChatGlassListHeaderImpl<TKey extends string = string>({
  searchQuery,
  onSearchChange,
  onComposePress,
  composeIcon,
  sortOption,
  sortOptions,
  onSortChange,
  hideSortRow = false,
  placeholder = 'Search conversations',
  rightActions,
  containerStyle,
}: ChatGlassListHeaderProps<TKey>) {
  const { colors } = useThemeColors();
  const [isSortModalVisible, setSortModalVisible] = useState(false);
  const [sortAnchor, setSortAnchor] = useState<AnchorRect | null>(null);
  const sortButtonRef = useRef<View>(null);

  const resolvedOptions: SortOptionItem<TKey>[] = (sortOptions ?? (DEFAULT_SORT_OPTIONS as unknown as SortOptionItem<TKey>[]));
  const currentSortLabel = useMemo(() => {
    if (!sortOption) return resolvedOptions[0]?.label ?? '';
    if (sortOptions) {
      return resolvedOptions.find((opt) => opt.key === sortOption)?.label ?? resolvedOptions[0]?.label ?? '';
    }
    return getSortOptionLabel(sortOption as unknown as MessageSortOption);
  }, [sortOption, sortOptions, resolvedOptions]);

  const handleSortSelect = (key: TKey) => {
    onSortChange?.(key);
    setSortModalVisible(false);
  };

  const openSort = () => {
    if (IS_WEB && sortButtonRef.current) {
      sortButtonRef.current.measureInWindow((x, y, w, h) => {
        setSortAnchor({ x, y, w, h });
        setSortModalVisible(true);
      });
    } else {
      setSortModalVisible(true);
    }
  };

  const showSortRow = !hideSortRow && !!onSortChange;

  const renderSortOptions = () => (
    <View>
      {resolvedOptions.map((option) => (
        <TouchableOpacity
          key={option.key}
          style={[
            styles.sortOption,
            { borderBottomColor: colors.separator },
            sortOption === option.key && { backgroundColor: colors.accent.blueBgLight },
          ]}
          onPress={() => handleSortSelect(option.key)}
        >
          <Text
            style={[
              styles.sortOptionText,
              { color: colors.text.primary },
              sortOption === option.key && styles.sortOptionTextSelected,
            ]}
          >
            {option.label}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={[styles.searchRow, { borderBottomColor: colors.separator }]}>
        <View
          style={[
            styles.searchContainer,
            { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder },
          ]}
        >
          <SearchIcon width={20} height={20} color={colors.text.muted} />
          <TextInput
            style={[styles.searchInput, { color: colors.text.primary }]}
            placeholder={placeholder}
            placeholderTextColor={colors.text.subtle}
            value={searchQuery}
            onChangeText={onSearchChange}
          />
        </View>
        {rightActions}
        {onComposePress ? (
          <TouchableOpacity
            style={styles.composeButton}
            onPress={onComposePress}
            activeOpacity={0.8}
          >
            {composeIcon ?? <PencilIcon width={20} height={20} color={colors.text.primary} />}
          </TouchableOpacity>
        ) : null}
      </View>

      {showSortRow && (
        <View style={styles.sortRow}>
          <View ref={sortButtonRef} style={styles.sortButtonAnchor}>
            <TouchableOpacity
              style={styles.sortButton}
              onPress={openSort}
              activeOpacity={0.7}
            >
              <Text style={[styles.sortText, { color: colors.text.subtle }]}>{currentSortLabel}</Text>
              <ChevronDownIcon width={16} height={16} color={colors.text.subtle} />
            </TouchableOpacity>
          </View>
        </View>
      )}

      {IS_WEB ? (
        <AnchoredDropdown
          isOpen={isSortModalVisible}
          anchor={sortAnchor}
          width={220}
          maxHeight={320}
          onRequestClose={() => setSortModalVisible(false)}
        >
          {renderSortOptions()}
        </AnchoredDropdown>
      ) : (
        <Modal
          visible={isSortModalVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setSortModalVisible(false)}
        >
          <Pressable style={styles.modalOverlay} onPress={() => setSortModalVisible(false)}>
            <View style={[styles.sortModal, { backgroundColor: colors.card.bg, borderColor: colors.card.border }]}>
              {renderSortOptions()}
            </View>
          </Pressable>
        </Modal>
      )}
    </View>
  );
}

export const ChatGlassListHeader = React.memo(ChatGlassListHeaderImpl) as typeof ChatGlassListHeaderImpl;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 20,
    borderBottomWidth: 1,
  },
  searchContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 12,
    height: 40,
    paddingHorizontal: 14,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    ...REDESIGN_TYPOGRAPHY.body,
    padding: 0,
  },
  composeButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sortRow: {
    paddingTop: 16,
    paddingBottom: 8,
  },
  sortButtonAnchor: {
    alignSelf: 'flex-start',
  },
  sortButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  sortText: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
  },
  sortModal: {
    borderRadius: 16,
    borderWidth: 1,
    overflow: 'hidden',
  },
  sortOption: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  sortOptionText: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  sortOptionTextSelected: {
    fontWeight: '600',
    color: PRIMITIVE_COLORS.resioBlue,
  },
});
